import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { PermitVehicleScope } from "common/types/permits";
import { ReviewVehicle } from "../types";

export interface CoveredVehiclesSectionProps {
  vehicleScope: PermitVehicleScope;
  vehicles: ReviewVehicle[];
  loading: boolean;
}

const VehicleListSkeleton: React.FC = () => (
  <List dense disablePadding>
    {[1, 2, 3].map((i) => (
      <ListItem key={i} disableGutters>
        <ListItemIcon sx={{ minWidth: 40 }}>
          <Skeleton variant="circular" width={24} height={24} />
        </ListItemIcon>
        <ListItemText
          primary={<Skeleton variant="text" width="60%" />}
          secondary={<Skeleton variant="text" width="40%" />}
        />
      </ListItem>
    ))}
  </List>
);

const formatVehicleDisplay = (vehicle: ReviewVehicle): string => {
  const parts: string[] = [];
  if (vehicle.year) {
    parts.push(String(vehicle.year));
  }
  if (vehicle.make) {
    parts.push(vehicle.make);
  }
  if (vehicle.model) {
    parts.push(vehicle.model);
  }
  return parts.length > 0 ? parts.join(" ") : "Vehicle";
};

export const CoveredVehiclesSection: React.FC<CoveredVehiclesSectionProps> = ({ vehicleScope, vehicles, loading }) => {
  const isPerAccount = vehicleScope === "per_account";

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" component="h3" gutterBottom>
          Covered Vehicles
        </Typography>

        {isPerAccount ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              p: 2,
              bgcolor: "primary.50",
              borderRadius: 1,
            }}
          >
            <InfoOutlinedIcon color="primary" sx={{ mt: 0.25 }} />
            <Box>
              <Typography variant="body1" fontWeight={500}>
                All vehicles in your account are covered by this permit
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Any vehicles you add to your account will automatically be covered.
              </Typography>
            </Box>
          </Box>
        ) : loading ? (
          <VehicleListSkeleton />
        ) : vehicles.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No vehicles selected
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {vehicles.length} {vehicles.length === 1 ? "vehicle" : "vehicles"} selected
            </Typography>
            <FixedSizeList
              height={Math.min(vehicles.length * 60, 300)}
              width="100%"
              itemSize={60}
              itemCount={vehicles.length}
              overscanCount={5}
            >
              {({ index, style }: ListChildComponentProps) => {
                const vehicle = vehicles[index];
                return (
                  <ListItem key={vehicle.id} disableGutters style={style}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <DirectionsCarIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={formatVehicleDisplay(vehicle)}
                      secondary={`${vehicle.licensePlate} (${vehicle.stateCode})`}
                    />
                  </ListItem>
                );
              }}
            </FixedSizeList>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CoveredVehiclesSection;
